;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import type * as React from "react";
import type { FieldValues, Path } from "react-hook-form";
import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import type { BaseFormHelperProps } from "./types";

interface InputProps extends Omit<React.ComponentProps<"input">, "className"> {
	className?: string;
	beforeIcon?: React.ReactNode;
	afterIcon?: React.ReactNode;
	wrapperClassName?: string;
}

interface FormInputProps<TFieldValues extends FieldValues = FieldValues>
	extends BaseFormHelperProps<TFieldValues>,
		Omit<InputProps, "form"> {
	name: Path<TFieldValues>;
	labelHelp?: React.ReactNode;
}

function InputIcon({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"absolute flex h-full items-center text-muted-foreground/70 [&>svg]:size-4 [&>svg]:stroke-[1.5] peer-aria-invalid:text-destructive peer-[&:not(:placeholder-shown):not(:focus):invalid]:text-destructive",
				className,
			)}
			data-slot="input-icon"
		>
			{children}
		</div>
	);
}

function Input({
	className,
	type,
	beforeIcon,
	afterIcon,
	wrapperClassName,
	...props
}: InputProps) {
	return (
		<div className={cn("relative flex items-center gap-2", wrapperClassName)}>
			<input
				className={cn(
					"file:text-foreground placeholder:text-muted-foreground placeholder:text-sm selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-10 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
					"focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
					"aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive peer",
					beforeIcon && "pl-9",
					afterIcon && "pr-9",
					className,
				)}
				data-slot="input"
				type={type}
				{...props}
			/>
			{beforeIcon && <InputIcon className="left-3">{beforeIcon}</InputIcon>}
			{afterIcon && <InputIcon className="right-3">{afterIcon}</InputIcon>}
		</div>
	);
}

function FormInput<TFieldValues extends FieldValues = FieldValues>({
	form,
	label,
	description,
	name,
	className,
	labelHelp,
	...inputProps
}: FormInputProps<TFieldValues>) {
	return (
		<FormField
			control={form.control}
			name={name}
			render={({ field }) => (
				<FormItem className={cn(className)}>
					{label && (
						<div className="flex items-center gap-2">
							<FormLabel className="m-0">{label}</FormLabel>
							{labelHelp}
						</div>
					)}
					<FormControl>
						<Input {...inputProps} {...field} />
					</FormControl>
					{description && <FormDescription>{description}</FormDescription>}
					<FormMessage />
				</FormItem>
			)}
		/>
	);
}

export { FormInput, type FormInputProps, Input, type InputProps };
